import { DASHBOARD_APPLIED_FILTERS_STORAGE_KEY } from '../constants/constants';

const EMPTY_STORED_DASHBOARD_FILTERS = {
  filterValues: {},
  cubeFilters: [],
};

const getDashboardAppliedFiltersRecordKey = dashboardId => {
  if (!dashboardId) {
    return '';
  }

  const clientId = localStorage.getItem('client_id') || '';
  const userId = localStorage.getItem('user_id') || '';
  return `${clientId}:${userId}:${dashboardId}`;
};

const readDashboardAppliedFiltersStore = () => {
  try {
    const raw = localStorage.getItem(DASHBOARD_APPLIED_FILTERS_STORAGE_KEY);
    if (!raw) {
      return {};
    }

    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const writeDashboardAppliedFiltersStore = store => {
  localStorage.setItem(
    DASHBOARD_APPLIED_FILTERS_STORAGE_KEY,
    JSON.stringify(store)
  );
};

export const getStoredDashboardAppliedFilters = dashboardId => {
  const recordKey = getDashboardAppliedFiltersRecordKey(dashboardId);
  if (!recordKey) {
    return { ...EMPTY_STORED_DASHBOARD_FILTERS };
  }

  const entry = readDashboardAppliedFiltersStore()[recordKey];
  if (!entry || typeof entry !== 'object') {
    return { ...EMPTY_STORED_DASHBOARD_FILTERS };
  }

  return {
    filterValues:
      entry.filterValues && typeof entry.filterValues === 'object'
        ? entry.filterValues
        : {},
    cubeFilters: Array.isArray(entry.cubeFilters) ? entry.cubeFilters : [],
  };
};

export const setStoredDashboardAppliedFilters = (
  dashboardId,
  { filterValues = {}, cubeFilters = [] } = EMPTY_STORED_DASHBOARD_FILTERS
) => {
  const recordKey = getDashboardAppliedFiltersRecordKey(dashboardId);
  if (!recordKey) {
    return;
  }

  const store = readDashboardAppliedFiltersStore();
  store[recordKey] = {
    filterValues,
    cubeFilters,
  };
  writeDashboardAppliedFiltersStore(store);
};

export const clearStoredDashboardAppliedFilters = dashboardId => {
  const recordKey = getDashboardAppliedFiltersRecordKey(dashboardId);
  if (!recordKey) {
    return;
  }

  const store = readDashboardAppliedFiltersStore();
  if (!Object.prototype.hasOwnProperty.call(store, recordKey)) {
    return;
  }

  delete store[recordKey];
  writeDashboardAppliedFiltersStore(store);
};

export const getDimensionDisplayLabel = dimension =>
  dimension?.shortTitle || dimension?.title || dimension?.name || 'Filter';

export const findFilterDimensionByName = (groupedOptions = [], name) => {
  if (!name) {
    return null;
  }

  for (const group of Array.isArray(groupedOptions) ? groupedOptions : []) {
    const match = (Array.isArray(group?.items) ? group.items : []).find(
      item => item.value === name
    );
    if (match) {
      return match;
    }
  }

  return null;
};

export const mapCubesToGroupedFilterOptions = (cubes = []) =>
  (Array.isArray(cubes) ? cubes : [])
    .map(cube => {
      const items = (Array.isArray(cube?.dimensions) ? cube.dimensions : [])
        .filter(
          dimension =>
            dimension?.isVisible !== false && !dimension?.meta?.foreignKeyField
        )
        .map(dimension => ({
          label: getDimensionDisplayLabel(dimension),
          value: dimension.name,
          type: dimension.type,
          title: getDimensionDisplayLabel(dimension),
          shortTitle: dimension.shortTitle,
          group: cube.title || cube.name,
        }))
        .filter(item => item.value);

      return {
        label: cube.title || cube.name,
        items,
      };
    })
    .filter(group => group.label && group.items.length);

export const getParentFilterOptions = (groupedOptions = []) =>
  (Array.isArray(groupedOptions) ? groupedOptions : []).map(group => ({
    label: group.label,
    value: group.label,
  }));

export const getChildFilterOptions = (
  groupedOptions = [],
  parentValue,
  excludeNames = []
) => {
  if (!parentValue) {
    return [];
  }

  const group = (Array.isArray(groupedOptions) ? groupedOptions : []).find(
    item => item.label === parentValue
  );
  const exclude = new Set(excludeNames);

  return (Array.isArray(group?.items) ? group.items : [])
    .filter(item => item?.value && !exclude.has(item.value))
    .map(item => ({
      label: getDimensionDisplayLabel(item),
      value: item.value,
    }));
};

export const findGroupedFilterItem = (
  groupedOptions = [],
  parentValue,
  childValue
) => {
  const group = (Array.isArray(groupedOptions) ? groupedOptions : []).find(
    item => item.label === parentValue
  );

  return (
    (Array.isArray(group?.items) ? group.items : []).find(
      item => item.value === childValue
    ) || null
  );
};

export const isCompleteDateRangeValue = value =>
  Array.isArray(value) &&
  value.length === 2 &&
  Boolean(value[0]) &&
  Boolean(value[1]);

const hasNonEmptyFilterValue = value => {
  if (Array.isArray(value)) {
    return value.some(
      item => item !== undefined && item !== null && item !== ''
    );
  }
  return value !== undefined && value !== null && value !== '';
};

export const mapDashboardFilterValuesToCubeFilters = (
  appliedDimensions = [],
  filterValues = {}
) =>
  (Array.isArray(appliedDimensions) ? appliedDimensions : [])
    .map(dimension => {
      const rawValue = filterValues?.[dimension?.name];
      if (rawValue === undefined || rawValue === null || rawValue === '') {
        return null;
      }

      if (dimension?.type === 'time') {
        if (!isCompleteDateRangeValue(rawValue) || !dimension?.name) {
          return null;
        }

        return {
          member: dimension.name,
          operator: 'inDateRange',
          values: [String(rawValue[0]), String(rawValue[1])],
        };
      }

      const valueList = Array.isArray(rawValue) ? rawValue : [rawValue];
      const values = valueList
        .map(value =>
          value === undefined || value === null ? '' : String(value)
        )
        .filter(Boolean);

      if (!values.length || !dimension?.name) {
        return null;
      }

      return {
        member: dimension.name,
        operator: 'equals',
        values,
      };
    })
    .filter(Boolean);

export const hasDashboardFilterValues = (
  filterValues = {},
  appliedDimensions = []
) => {
  if (Array.isArray(appliedDimensions) && appliedDimensions.length) {
    return appliedDimensions.some(dimension => {
      const value = filterValues?.[dimension?.name];
      if (dimension?.type === 'time') {
        return isCompleteDateRangeValue(value);
      }
      return hasNonEmptyFilterValue(value);
    });
  }

  return Object.values(filterValues || {}).some(hasNonEmptyFilterValue);
};

export const normalizeSavedFilterConfig = response => {
  if (!response) {
    return [];
  }

  const filters = Array.isArray(response)
    ? response
    : response.filters || response.data?.filters || response.data || [];

  if (!Array.isArray(filters)) {
    return [];
  }

  return filters
    .filter(filter => filter?.name)
    .map(filter => ({
      name: filter.name,
      title: filter.shortTitle || filter.title || filter.name,
      type: filter.type,
      group: filter.group,
    }));
};
